/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/**
 * @file: NavBar 适配动态状态栏高度
 * @author: zhangjie13
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { debounceOpenShare, getCls, isInApp } from '@utils/helpers';
import yoda from '@ks/yoda-js-sdk';
import useDeviceInfo from '@/utils/useDeviceInfo';
import NavBarShadow from '@/components/NavBarShadow';
import IconFont from '@/components/Iconfont';
import { getTargetUrl, getUrlParam } from '@/utils/url';
import { debugConsole } from '@aio/utils';

import './index.scss';

interface IShareTipConfig {
  time?: number;
  className?: string;
  text: React.ReactNode;
  visible?: boolean;
}

interface INavBarProps {
  title?: string;
  showShare?: boolean;
  theme?: 'transparent' | 'white' | 'scroll' | 'transparent-bg';
  type?: 'back' | 'close';
  isBackHome?: boolean;
  isHome?: boolean;
  sharePath?: string;
  handleGoBack?: () => void;
  children?: React.ReactNode;
  shareConfig?: {
    [key: string]: string;
  };
  shareComponent?: React.ReactNode;
  onShare?: () => void;
  showShadow?: boolean;
  shareTip?: IShareTipConfig; // 右上角分享气泡配置
  showBottomLine?: boolean;
}

const NavBar = ({
  title = '',
  showShare = false,
  theme = 'white',
  type = 'back',
  isBackHome = false,
  sharePath,
  handleGoBack,
  children,
  shareConfig,
  shareComponent,
  onShare,
  showShadow = true,
  shareTip,
  showBottomLine
}: INavBarProps) => {
  const [isShareTip, setShareTipStatus] = useState<boolean>(false);
  const history = useHistory();
  const shareTipTimeRef = useRef<any>(null);
  const isShowShare = showShare && isInApp;

  const { statusBarHeight, getAdaptiveCssPx } = useDeviceInfo(true);

  useEffect(() => {
    if (shareTip) {
      setShareTipStatus(true);

      shareTipTimeRef.current = setTimeout(() => {
        setShareTipStatus(false);
      }, shareTip.time || 3000);
    }

    return () => {
      shareTipTimeRef.current && clearTimeout(shareTipTimeRef.current);
    };
  }, [shareTip]);

  const goBack = () => {
    if (type === 'close') {
      if (yoda.isInYoda) {
        yoda.ready().then(() => {
          yoda.webview.close();
        });
      }
    } else if (isBackHome) {
      const url = getTargetUrl(
        `${window.location.origin}?layoutType=4&enableWK=1&hyId=healthy-channel`
      );
      history.replace(url);
    } else if (yoda.isInYoda) {
      yoda.ready().then(() => {
        yoda.webview.backOrClose();
      });
    } else {
      history.goBack();
    }
  };

  const handleShare = () => {
    debounceOpenShare(sharePath, shareConfig);
  };

  return (
    <>
      <div
        className={`nav-bar ${theme} ${
          showBottomLine ? 'bottom-line' : ''
        } ${getCls()}`}
        style={
          statusBarHeight > 0
            ? { paddingTop: getAdaptiveCssPx(statusBarHeight) }
            : undefined
        }
      >
        <button
          className="nav-bar-back"
          type="button"
          onClick={handleGoBack || goBack}
        >
          <IconFont icon="common_nav_back_black" />
        </button>
        <h1>{title}</h1>
        <div className="children-wrapper">{children}</div>
        <div className="button-wrapper">
          {isShowShare && (
            <button
              className="nav-bar-default-share"
              type="button"
              onClick={onShare || handleShare}
            >
              <IconFont icon="common_base_share_24" />
              <h3>分享</h3>
              {isShareTip && (
                <div
                  className={`share-tip ${shareTip?.className || ''}`}
                  style={
                    statusBarHeight > 0 ? { top: statusBarHeight } : undefined
                  }
                >
                  {shareTip?.text}
                </div>
              )}
            </button>
          )}
          {shareComponent}
        </div>
      </div>
      {showShadow && <NavBarShadow />}
    </>
  );
};

export default NavBar;
